/**
 * The corrected live view: a cabinet camera's frame as a person would see it.
 *
 * A cabinet records through a fisheye under a strongly blue LED strip, and the
 * recognizer works on exactly that - galleries, work areas, training rows are
 * all in native frame coordinates. This module only changes the *view*: it
 * straightens the fisheye into a pinhole picture and neutralises the white
 * balance, from the lens and light that `tools/synth` (vision-ml) measured
 * and stored as the device's calibration (`GET devices/{id}/calibration`,
 * docs/tasks/39). Overlays drawn in native coordinates go through `toRectBox`
 * / `toRectPoint` so they land on the same product in the corrected picture.
 *
 * The numbers must agree with `tools/synth/rectify_view.py` (vision-ml):
 * `test_rectify_view.py` there runs this file under node and compares.
 */

// A Kannala-Brandt lens (OpenCV fisheye) at the size of the frame shown.
export function lensFor(doc, width, height) {
  if (!doc || doc.model !== "kannala_brandt") return null;
  const sx = width / doc.width, sy = height / doc.height;
  return {
    width, height,
    fx: doc.fx * sx, fy: doc.fy * sy,
    cx: (doc.cx + 0.5) * sx - 0.5, cy: (doc.cy + 0.5) * sy - 0.5,
    k: (doc.k || [0, 0, 0, 0]).map(Number),
    thetaMax: ((doc.theta_max_deg ?? 100) * Math.PI) / 180,
  };
}

function distort(lens, theta) {
  const [k1, k2, k3, k4] = lens.k;
  const t2 = theta * theta;
  return theta * (1 + t2 * (k1 + t2 * (k2 + t2 * (k3 + t2 * k4))));
}

function undistort(lens, thetaD) {
  const [k1, k2, k3, k4] = lens.k;
  if (!k1 && !k2 && !k3 && !k4) return thetaD;
  let theta = thetaD;
  for (let i = 0; i < 20; i += 1) {
    const t2 = theta * theta;
    const f = theta * (1 + t2 * (k1 + t2 * (k2 + t2 * (k3 + t2 * k4)))) - thetaD;
    let df = 1 + t2 * (3 * k1 + t2 * (5 * k2 + t2 * (7 * k3 + t2 * 9 * k4)));
    if (Math.abs(df) < 1e-9) df = 1e-9;
    const step = f / df;
    theta -= step;
    if (Math.abs(step) < 1e-10) break;
  }
  return theta;
}

// The pinhole camera of the corrected view: hfov degrees across the width,
// square pixels, the same frame size.
export function rectK(lens, hfovDeg) {
  const f = (lens.width / 2) / Math.tan((hfovDeg * Math.PI) / 180 / 2);
  return { f, cx: (lens.width - 1) / 2, cy: (lens.height - 1) / 2 };
}

// Where a pixel of the corrected view comes from in the native frame
// (floats), or null for a ray the lens does not see.
export function srcPixel(lens, hfovDeg, u, v) {
  const K = rectK(lens, hfovDeg);
  const x = (u - K.cx) / K.f, y = (v - K.cy) / K.f;
  const rxy = Math.hypot(x, y);
  const theta = Math.atan2(rxy, 1);
  if (theta > lens.thetaMax) return null;
  const scale = rxy > 1e-12 ? distort(lens, theta) / rxy : 0;
  return [lens.fx * x * scale + lens.cx, lens.fy * y * scale + lens.cy];
}

// A native pixel -> its place in the corrected view (floats), or null when
// the ray points behind the pinhole.
export function toRectPoint(lens, hfovDeg, u, v) {
  const mx = (u - lens.cx) / lens.fx, my = (v - lens.cy) / lens.fy;
  const thetaD = Math.hypot(mx, my);
  const theta = undistort(lens, thetaD);
  const s = thetaD > 1e-12 ? Math.sin(theta) / thetaD : 1;
  const z = Math.cos(theta);
  if (z <= 1e-6) return null;
  const K = rectK(lens, hfovDeg);
  return [(mx * s / z) * K.f + K.cx, (my * s / z) * K.f + K.cy];
}

// A box in frame-normalised coordinates -> its bounding box in the corrected
// view, normalised; the edges are sampled because a straight native edge
// bends. null when any part is out of the pinhole's sight.
export function toRectBox(lens, hfovDeg, box) {
  if (!box || box.length !== 4) return null;
  const [x0, y0, x1, y1] = box;
  const pts = [];
  for (let i = 0; i <= 8; i += 1) {
    const t = i / 8;
    pts.push([x0 + (x1 - x0) * t, y0], [x0 + (x1 - x0) * t, y1], [x0, y0 + (y1 - y0) * t], [x1, y0 + (y1 - y0) * t]);
  }
  let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
  for (const [nx, ny] of pts) {
    const p = toRectPoint(lens, hfovDeg, nx * lens.width - 0.5, ny * lens.height - 0.5);
    if (!p) return null;
    u0 = Math.min(u0, p[0]); v0 = Math.min(v0, p[1]); u1 = Math.max(u1, p[0]); v1 = Math.max(v1, p[1]);
  }
  return [(u0 + 0.5) / lens.width, (v0 + 0.5) / lens.height, (u1 + 0.5) / lens.width, (v1 + 0.5) / lens.height];
}

export function toRectNormPoint(lens, hfovDeg, [nx, ny]) {
  const p = toRectPoint(lens, hfovDeg, nx * lens.width - 0.5, ny * lens.height - 0.5);
  return p ? [(p[0] + 0.5) / lens.width, (p[1] + 0.5) / lens.height] : null;
}

// Nearest-neighbour remap table: for every pixel of the corrected view the
// index of the native pixel it shows, -1 where there is none.
export function buildRemap(lens, hfovDeg) {
  const { width: w, height: h } = lens;
  const table = new Int32Array(w * h);
  for (let v = 0; v < h; v += 1) {
    for (let u = 0; u < w; u += 1) {
      const s = srcPixel(lens, hfovDeg, u, v);
      let idx = -1;
      if (s) {
        const su = Math.round(s[0]), sv = Math.round(s[1]);
        if (su >= 0 && su < w && sv >= 0 && sv < h) idx = sv * w + su;
      }
      table[v * w + u] = idx;
    }
  }
  return table;
}

const srgbToLinear = (x) => (x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (x) => (x <= 0.0031308 ? x * 12.92 : 1.055 * x ** (1 / 2.4) - 0.055);

// Per-channel lookup tables: sRGB byte -> linear × gain -> sRGB byte. The
// gains (`light.white_gains_rgb`) make the measured white sheets grey.
export function makeWbLut(gainsRgb) {
  if (!gainsRgb || gainsRgb.length !== 3) return null;
  return gainsRgb.map((g) => {
    const lut = new Uint8ClampedArray(256);
    for (let i = 0; i < 256; i += 1) lut[i] = Math.round(linearToSrgb(Math.min(1, srgbToLinear(i / 255) * g)) * 255);
    return lut;
  });
}

/**
 * Everything the pane needs for one camera at one frame size. `rect` and `wb`
 * switch the two corrections; a camera with no lens in the calibration gets
 * only the white balance, one with no light only the geometry.
 */
export function makeCorrector(cameraDoc, width, height, { hfovDeg = 100, rect = true, wb = true } = {}) {
  const lens = rect ? lensFor(cameraDoc?.lens, width, height) : null;
  const lut = wb ? makeWbLut(cameraDoc?.light?.white_gains_rgb) : null;
  if (!lens && !lut) return null;
  const remap = lens ? buildRemap(lens, hfovDeg) : null;
  return {
    width, height, hfovDeg, lens, lut, remap,
    mapBox: (box) => (lens ? toRectBox(lens, hfovDeg, box) : box),
    mapPoint: (pt) => (lens ? toRectNormPoint(lens, hfovDeg, pt) : pt),
  };
}

// Draw the corrected picture of `img` (an <img> or canvas of the corrector's
// size) onto `canvas`. Costs one pass over the pixels (~3 ms at 640×480).
export function drawCorrected(img, canvas, corrector, scratch) {
  const { width: w, height: h, remap, lut } = corrector;
  const sc = scratch || document.createElement("canvas");
  sc.width = w; sc.height = h;
  const sctx = sc.getContext("2d", { willReadFrequently: true });
  sctx.drawImage(img, 0, 0, w, h);
  const src = sctx.getImageData(0, 0, w, h).data;
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  const out = ctx.createImageData(w, h);
  const d = out.data;
  const n = w * h;
  for (let i = 0; i < n; i += 1) {
    const j = remap ? remap[i] : i;
    const o = i * 4;
    if (j < 0) { d[o] = 0; d[o + 1] = 0; d[o + 2] = 0; d[o + 3] = 255; continue; }
    const s = j * 4;
    if (lut) { d[o] = lut[0][src[s]]; d[o + 1] = lut[1][src[s + 1]]; d[o + 2] = lut[2][src[s + 2]]; }
    else { d[o] = src[s]; d[o + 1] = src[s + 1]; d[o + 2] = src[s + 2]; }
    d[o + 3] = 255;
  }
  ctx.putImageData(out, 0, 0);
  return sc;
}
